import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import qs from 'qs';
import moment from 'moment';

import {
  Flex,
  H2,
  H5,
  Text,
  CPanel,
  CDialog,
  CSkeleton,
} from '@appcues/component-library';

import {
  getPermissionLabelForUser,
  canManageAccountUsers,
} from 'helpers/account-management';
import {
  selectUserIsSystemAdmin,
  selectUser,
  selectUserId,
} from 'reducers/user';
import { selectAccountFeature } from 'reducers/account/features';
import { removeAccountUserAndFlushUsers } from 'actions/accountManagement';
import { updateUserRole, acceptTransfer } from 'actions/account/users';
import { hideModal, showModal } from 'actions/currentModal';

import Tag, { TagTypes } from 'components/Common/Tag';
import CoolTip from 'components/Common/CoolTip';
import Table from 'components/Common/UI/Table';
import { ManageUserDropdownButton } from 'components/Team/ManageUserDropdown';
import UserPermissionsSelectInput from 'components/Team/UserPermissionsSelectInput';
import AvatarImg from 'components/Common/AvatarImg';

import ConfirmRemoveUseFromTeamModal from 'components/ModalContainer/ConfirmRemoveUseFromTeamModal';

import {
  selectAccountUsers,
  selectAccountUsersSynced,
} from 'reducers/account/users';

import { EDITOR_PERMISSIONS_ROLE } from 'constants/features';
import {
  ACCOUNT_PERMISSION_OPTIONS,
  LIMITED_ACCOUNT_PERMISSION_OPTIONS,
  ACCOUNT_USER_STATUSES,
} from 'constants/accountManagement/permissions';

import AcceptTransferModal from 'components/Team/AcceptTransferModal';

import { asPromised } from 'utils/as-promised';
import InviteManager from './InviteManager';
import Settings from './Settings';

const { INVITED, PENDING } = ACCOUNT_USER_STATUSES;

export const DEFAULT_AVATAR_URL =
  'https://i2.wp.com/my.appcues.com/images/avatar.png?ssl=1';

const DisplayName = ({ name, isInvited }) => {
  if (!name) {
    return <CSkeleton height="18px" width="100px" />;
  }
  return (
    <Text is="div" maxWidth={150} truncate>
      <strong>{isInvited ? 'Invited user' : name}</strong>
    </Text>
  );
};

const LastSeenContent = ({ user, date }) => {
  switch (user.status) {
    case 'pending':
      return (
        <Tag title="Hasn't accepted invite yet" type={TagTypes.PENDING}>
          <H5 color="$yellow-xtradark">INVITE PENDING</H5>
        </Tag>
      );
    case 'invited':
      return <div>{date}</div>;
    case 'enabled':
      return <div>{date}</div>;
    default:
      return (
        <Tag title="Hasn't accepted invite yet" type={TagTypes.PENDING}>
          <H5 color="$yellow-xtradark">INVITE PENDING</H5>
        </Tag>
      );
  }
};

export class Team extends Component {
  state = {
    currentModal: null,
    selectedUser: null,
  };

  componentDidMount() {
    const { transferToAccount } = this.props;
    if (transferToAccount) {
      this.setState({ currentModal: 'TRANSFER_TO_ACCOUNT' });
    }
  }

  handleSelectDropdownOption = (value, user) => {
    if (user) {
      this.setState({
        currentModal: 'CONFIRM_REMOVE_USER',
        selectedUser: user,
      });
    }
  };

  handleSelectPermission = ({ userId, role }) => {
    const { onSelectPermission } = this.props;
    onSelectPermission({
      userId,
      role,
    });
  };

  handleAcceptTransfer = ({ accountId }) => {
    const { onAcceptTransfer } = this.props;
    onAcceptTransfer(accountId).catch(() =>
      this.setState({ currentModal: 'ERROR' })
    );
  };

  handleHideModal = () => {
    this.setState({ currentModal: null, selectedUser: null });
  };

  render() {
    const {
      isLoaded,
      loggedInUserId,
      accountUsers,
      tableData,
      hasEditorPermissionsRole,
      canManageAcctUsers,
      transferToAccount,
    } = this.props;
    const { currentModal, selectedUser } = this.state;

    const permissionArray = hasEditorPermissionsRole
      ? ACCOUNT_PERMISSION_OPTIONS
      : LIMITED_ACCOUNT_PERMISSION_OPTIONS;

    const PermissionsToolTipText = () => {
      return permissionArray.map(permission => (
        <span key={permission.value}>
          <strong>{permission.label}: </strong>
          {permission.permissionDescription}
          <br />
        </span>
      ));
    };

    return (
      <>
        {currentModal === 'TRANSFER_TO_ACCOUNT' && (
          <AcceptTransferModal
            accountId={transferToAccount}
            onSubmit={this.handleAcceptTransfer}
            onClose={() => this.setState({ currentModal: null })}
          />
        )}
        {currentModal === 'CONFIRM_REMOVE_USER' && (
          <ConfirmRemoveUseFromTeamModal
            onClose={this.handleHideModal}
            selectedUser={selectedUser}
          />
        )}
        {currentModal === 'ERROR' && (
          <CDialog
            title="We encountered an error"
            onPrimaryAction={() => this.setState({ currentModal: null })}
          >
            <p>
              Please ensure that your invitation was not revoked by a member of
              Account {transferToAccount}.
            </p>
            <p>
              Sorry about that. Please try again, and if the problem persists,
              don&apos;t hesitate to contact us.
            </p>
          </CDialog>
        )}
        <CPanel>
          <CPanel.Header>
            <H2>Team</H2>
            <CPanel.Header.Actions>
              <Settings />
              <InviteManager />
            </CPanel.Header.Actions>
          </CPanel.Header>
          {isLoaded && (
            <Table
              panel
              defaultSortColumn="fullname"
              containerStyles={{ marginTop: 20 }}
              data={tableData}
              columns={[
                {
                  Header: 'Teammate',
                  accessor: 'fullname',
                  sortable: true,
                  maxWidth: 150,
                  Cell: ({ row: { values, original } }) => {
                    const isInvited =
                      original.status === INVITED ||
                      original.status === PENDING;
                    return (
                      <ToolTip
                        position="right"
                        disabled={!isInvited}
                        tip="This user's personal information will display once they accept their transfer invitation"
                      >
                        <Flex
                          alignItems="center"
                          justifyContent="left"
                          whiteSpace="nowrap"
                          overflow="hidden"
                          textOverflow="ellipsis"
                        >
                          <AvatarImg
                            marginRight={15}
                            width={25}
                            height={25}
                            userId={values.id}
                          />
                          <DisplayName
                            name={values.fullname || values.email}
                            isInvited={isInvited}
                          />
                        </Flex>
                      </ToolTip>
                    );
                  },
                },
                {
                  Header: 'Email',
                  accessor: 'email',
                  sortable: true,
                  maxWidth: 200,
                  Cell: ({ row: { values } }) => (
                    <Text is="div">
                      {values.email || <CSkeleton height={18} width="75%" />}
                    </Text>
                  ),
                },
                {
                  Header: (
                    <Flex alignItems="center">
                      Permissions
                      <ToolTip
                        tip={<PermissionsToolTipText />}
                        width="360px"
                        position="bottom"
                        marginRight={-82}
                        lineHeight="165%"
                      />
                    </Flex>
                  ),
                  accessor: 'permissions',
                  Cell: ({ row: { original } }) => {
                    return canManageAcctUsers &&
                      loggedInUserId !== original.id ? (
                      <UserPermissionsSelectInput
                        accountUsers={accountUsers}
                        user={original}
                        onSelectPermission={this.handleSelectPermission}
                        disabled={loggedInUserId === original.id}
                        hasEditorPermissionsRole={hasEditorPermissionsRole}
                      />
                    ) : (
                      getPermissionLabelForUser({
                        user: original,
                        accountUsers,
                      })
                    );
                  },
                },
                {
                  Header: 'Last seen',
                  accessor: 'lastSeenTimestamp',
                  sortable: true,
                  Cell: ({ row: { original } }) => {
                    const date = moment(original.lastSeenTimestamp).format(
                      'MMM D, YYYY'
                    );
                    return <LastSeenContent user={original} date={date} />;
                  },
                },
                {
                  accessor: 'id',
                  Cell: ({ row: { values } }) => {
                    if (canManageAcctUsers && loggedInUserId !== values.id) {
                      return (
                        <ManageUserDropdownButton
                          user={values}
                          onSelectDropdownOption={
                            this.handleSelectDropdownOption
                          }
                        />
                      );
                    }
                    return null;
                  },
                },
              ]}
            />
          )}
        </CPanel>
      </>
    );
  }
}

Team.propTypes = {
  loggedInUserId: PropTypes.string,
  accountUsers: PropTypes.objectOf(
    PropTypes.shape({
      role: PropTypes.string,
    })
  ),
  canManageAcctUsers: PropTypes.bool,
  hasEditorPermissionsRole: PropTypes.bool,
  transferToAccount: PropTypes.string,
  onAcceptTransfer: PropTypes.func,
  onSelectPermission: PropTypes.func,
  isLoaded: PropTypes.bool,
  actions: PropTypes.shape({
    showModal: PropTypes.func,
    hideModal: PropTypes.func,
    removeAccountUserAndFlushUsers: PropTypes.func,
  }),
};

export function mapStateToProps(state, { location: { search = '' } }) {
  const { acceptInviteToAccountId: transferToAccount } = qs.parse(search, {
    ignoreQueryPrefix: true,
  });
  const stateUsers = selectAccountUsers(state);
  const combinedAccountUsers = stateUsers
    ? Object.keys(stateUsers).map(userId => ({
        // order of spread matters:
        // Grab user's meta, but if anything more specific on account,
        // use the account specific information
        ...stateUsers[userId].meta,
        ...stateUsers[userId],
        id: userId,
      }))
    : [];

  const user = { id: selectUserId(state), ...selectUser(state) };

  const isLoaded = selectAccountUsersSynced(state);

  let tableData = [];
  let canManageAcctUsers = false;
  if (isLoaded) {
    tableData = combinedAccountUsers
      .filter(accountUser => {
        return (
          selectUserIsSystemAdmin(state) ||
          (stateUsers[user.id] &&
            stateUsers[user.id].role === 'ACCOUNT_ADMIN') ||
          (accountUser.status !== INVITED && accountUser.status !== PENDING)
        );
      })
      .map(accountUser => {
        return {
          ...accountUser,
          avatarUrl: accountUser.avatarUrl || DEFAULT_AVATAR_URL,
        };
      });

    canManageAcctUsers = canManageAccountUsers({
      user,
      accountUsers: stateUsers,
    });
  }

  return {
    loggedInUserId: user.id,
    tableData,
    accountUsers: stateUsers,
    hasEditorPermissionsRole: selectAccountFeature(
      state,
      EDITOR_PERMISSIONS_ROLE
    ),
    canManageAcctUsers,
    isLoaded,
    transferToAccount,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    onAcceptTransfer: accountId =>
      asPromised(dispatch, acceptTransfer({ accountId })),
    onSelectPermission: ({ userId, role }) =>
      asPromised(dispatch, updateUserRole({ userId, role })),
    actions: bindActionCreators(
      {
        removeAccountUserAndFlushUsers,
        hideModal,
        showModal,
      },
      dispatch
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Team);

const ToolTip = styled(CoolTip)`
  align-items: center;
  color: ${props => props.theme['$gray-5']};
  display: flex;
  padding-left: 8px;
`;
